import { STATUSPAGE } from '@repo/ui';
import {
  AdvertisingCreateUseCaseInterface,
  AdvertisingDeleteUseCaseInterface,
  AdvertisingListUseCaseInterface,
  AdvertisingUpdateUseCaseInterface,
  GetAllCoursesByUserUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

export const useAdvertising = () => {
  // service
  const { courseService, advertisingService } = useService();

  // schema
  const bannerValidationSchema = Yup.object().shape({
    photo: Yup.string().required('A imagem é obrigatória'),
    linkUrl: Yup.string()
      .url('Informe uma URL válida')
      .required('O link é obrigatório'),
    courseIds: Yup.array()
      .min(1, 'Selecione pelo menos um curso')
      .required('Selecione pelo menos um curso'),
    showInStudentApp: Yup.boolean(),
    showInProfessionalApp: Yup.boolean(),
  });

  const { entityId } = useParams<{ entityId: string }>();

  // Validação do arquivo
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  const maxSize = 5 * 1024 * 1024;

  // states
  const [imagePreview, setImagePreview] = useState<string>();
  const [courseStatus, setCourseStatus] = useState(STATUSPAGE.DONE);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [advertisingCreateStatus, setAdvertisingCreateStatus] = useState(
    STATUSPAGE.DONE,
  );

  // advertising list
  const [advertisingList, setAdvertisingList] =
    useState<AdvertisingListUseCaseInterface.Result[]>();

  const [advertisingListStatus, setAdvertisingListStatus] = useState(
    STATUSPAGE.LOADING,
  );

  // course list
  const [listCourses, setListCourses] =
    useState<GetAllCoursesByUserUseCaseInterface.Result>();

  const handleClose = () => {
    formik.resetForm();
    setImagePreview('');
    setIsModalOpen(!isModalOpen);
  };

  const formik = useFormik({
    initialValues: {
      organizationHashId: null,
      photo: null,
      linkUrl: null,
      courseIds: [] as Array<{ label: string; value: string }>,
      showInStudentApp: false,
      showInProfessionalApp: false,
    },
    validationSchema: bannerValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      requestCreateAdvertising({
        ...values,
        organizationHashId: entityId,
        courseIds: values.courseIds.map((course) => course.value),
      });
    },
  });

  // Função reutilizável para validar e processar imagem
  const validateAndProcessImage = useCallback(
    (file: File, onSuccess: (base64: string) => void) => {
      if (!allowedTypes.includes(file.type)) {
        alert('Apenas arquivos PNG, JPG ou JPEG são permitidos');
        return;
      }

      if (file.size > maxSize) {
        alert('O arquivo deve ter no máximo 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        onSuccess(base64String);
      };
      reader.readAsDataURL(file);
    },
    [allowedTypes, maxSize],
  );

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    validateAndProcessImage(file, (base64String) => {
      setImagePreview(base64String);
      formik.setFieldValue('photo', base64String);
    });
  };

  // list course
  const requestCourseList = useCallback(async () => {
    setCourseStatus(STATUSPAGE.LOADING);
    return courseService
      .getAllCoursesByUser({ entityId: entityId || '' })
      .then((res) => {
        setListCourses(res);
      })
      .finally(() => {
        setCourseStatus(STATUSPAGE.DONE);
      });
  }, []);

  // create advertising
  const requestCreateAdvertising = useCallback(
    async (params: AdvertisingCreateUseCaseInterface.Params) => {
      setAdvertisingCreateStatus(STATUSPAGE.LOADING);
      return advertisingService
        .advertisingCreate(params)
        .then((res) => {
          requestListAdvertising({
            organizationId: params.organizationHashId,
          });
          handleClose();
          notifySuccess(res?.message || 'Publicidade criada com sucesso!');
        })
        .catch((error) => {
          notifyError(error.message || 'Erro ao criar publicidade.');
        })
        .finally(() => {
          setAdvertisingCreateStatus(STATUSPAGE.DONE);
        });
    },
    [isModalOpen],
  );

  // delete advertising
  const requestDeleteAdvertising = useCallback(
    async (params: AdvertisingDeleteUseCaseInterface.Params) => {
      return advertisingService
        .advertisingDelete(params)
        .then((res) => {
          notifySuccess(res.message || 'Publicidade excluída com sucesso!');
          requestListAdvertising({ organizationId: entityId });
        })
        .catch((error) => {
          notifyError(error.message || 'Erro ao excluir publicidade.');
        });
    },
    [],
  );

  // list advertising
  const requestListAdvertising = useCallback(
    async (
      params: AdvertisingListUseCaseInterface.Params,
      options?: { silent?: boolean },
    ) => {
      const silent = options?.silent;
      if (!silent) setAdvertisingListStatus(STATUSPAGE.LOADING);
      return advertisingService
        .advertisingList(params)
        .then((res) => {
          setAdvertisingList(res);
        })
        .catch((error) => {
          notifyError(error.message || 'Erro ao listar publicidade.');
        })
        .finally(() => {
          if (!silent) setAdvertisingListStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // update advertising
  const requestUpdateAdvertising = useCallback(
    async (params: AdvertisingUpdateUseCaseInterface.Params) => {
      return advertisingService
        .advertisingUpdate(params)
        .then((res) => {
          notifySuccess(res?.message || 'Publicidade atualizada com sucesso!');
          requestListAdvertising(
            { organizationId: params.organizationHashId },
            { silent: true },
          );
        })
        .catch((error) => {
          notifyError(error.message || 'Erro ao atualizar publicidade.');
        });
    },
    [entityId],
  );

  return {
    formik,
    maxSize,
    entityId,
    listCourses,
    isModalOpen,
    imagePreview,
    allowedTypes,
    courseStatus,
    advertisingList,
    advertisingCreateStatus,
    advertisingListStatus,
    handleClose,
    setIsModalOpen,
    handleImageChange,
    validateAndProcessImage,
    requestCourseList,
    requestListAdvertising,
    requestDeleteAdvertising,
    requestUpdateAdvertising,
  };
};
